import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Search,
  Bell,
  Menu,
  Command,
  Settings,
  User,
  LogOut,
  Activity,
} from 'lucide-react';
import { useUIStore } from '@/store/uiStore';
import { useAuthStore } from '@/store/authStore';

export default function Header() {
  const navigate = useNavigate();
  const { setSidebarMobileOpen, toggleCommandPalette, toggleNotificationPanel } = useUIStore();
  const { admin, logout } = useAuthStore();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [unreadNotifications] = useState(5);

  return (
    <header className="sticky top-0 z-40 h-16 bg-slate-950/80 backdrop-blur-xl border-b border-slate-800/50">
      <div className="h-full px-4 lg:px-8 flex items-center justify-between gap-4">
        {/* Left section */}
        <div className="flex items-center gap-4">
          {/* Mobile menu button */}
          <button
            onClick={() => setSidebarMobileOpen(true)}
            className="lg:hidden p-2 rounded-lg hover:bg-slate-800/50 text-slate-400 hover:text-white transition-colors"
          >
            <Menu className="w-6 h-6" />
          </button>

          {/* Search / Command Palette trigger */}
          <button
            onClick={toggleCommandPalette}
            className="flex items-center gap-3 px-4 py-2 rounded-xl bg-slate-900/50 border border-slate-800/50 hover:border-slate-700/50 transition-colors group w-64 lg:w-80"
          >
            <Search className="w-4 h-4 text-slate-500 group-hover:text-slate-400" />
            <span className="text-sm text-slate-500 group-hover:text-slate-400 flex-1 text-left">
              Search anything...
            </span>
            <kbd className="hidden sm:inline-flex items-center gap-1 px-2 py-0.5 rounded bg-slate-800 text-slate-500 text-xs font-mono">
              <Command className="w-3 h-3" />K
            </kbd>
          </button>
        </div>

        {/* Right section */}
        <div className="flex items-center gap-2">

          {/* Real-time activity */}
          <button
            onClick={() => navigate('/real-time')}
            className="hidden md:flex p-2 rounded-lg hover:bg-slate-800/50 text-slate-400 hover:text-white transition-colors"
            title="Real-time Monitor"
          >
            <Activity className="w-5 h-5" />
          </button>

          {/* Notifications */}
          <button
            onClick={toggleNotificationPanel}
            className="relative p-2 rounded-lg hover:bg-slate-800/50 text-slate-400 hover:text-white transition-colors"
          >
            <Bell className="w-5 h-5" />
            {unreadNotifications > 0 && (
              <span className="absolute top-1 right-1 w-4 h-4 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">
                {unreadNotifications > 9 ? '9+' : unreadNotifications}
              </span>
            )}
          </button>

          {/* User menu */}
          <div className="relative">
            <button
              onClick={() => setShowUserMenu(!showUserMenu)}
              className="flex items-center gap-3 p-1.5 rounded-xl hover:bg-slate-800/50 transition-colors"
            >
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center">
                <span className="text-white font-semibold text-sm">
                  {admin?.name?.charAt(0) ?? 'A'}
                </span>
              </div>
              <div className="hidden md:block text-left">
                <p className="text-sm font-medium">{admin?.name ?? 'Admin'}</p>
                <p className="text-xs text-slate-500 capitalize">{admin?.role ?? 'admin'}</p>
              </div>
            </button>

            {/* Dropdown menu */}
            {showUserMenu && (
              <>
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setShowUserMenu(false)}
                />
                <motion.div
                  initial={{ opacity: 0, scale: 0.95, y: -10 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95, y: -10 }}
                  className="absolute right-0 top-full mt-2 w-56 rounded-xl bg-slate-900 border border-slate-800 shadow-xl z-50 overflow-hidden"
                >
                  <div className="p-3 border-b border-slate-800">
                    <p className="font-medium">{admin?.name ?? 'Admin'}</p>
                    <p className="text-sm text-slate-400">{admin?.email}</p>
                  </div>
                  <div className="p-1.5">
                    <button
                      onClick={() => {
                        setShowUserMenu(false);
                        navigate('/settings');
                      }}
                      className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800/50 transition-colors"
                    >
                      <User className="w-4 h-4" />
                      <span className="text-sm">Profile</span>
                    </button>
                    <button
                      onClick={() => {
                        setShowUserMenu(false);
                        navigate('/settings');
                      }}
                      className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800/50 transition-colors"
                    >
                      <Settings className="w-4 h-4" />
                      <span className="text-sm">Settings</span>
                    </button>
                  </div>
                  <div className="p-1.5 border-t border-slate-800">
                    <button
                      onClick={() => {
                        setShowUserMenu(false);
                        logout();
                      }}
                      className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-red-400 hover:text-red-300 hover:bg-red-500/10 transition-colors"
                    >
                      <LogOut className="w-4 h-4" />
                      <span className="text-sm">Logout</span>
                    </button>
                  </div>
                </motion.div>
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
